import { useQuery } from "@apollo/client";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink, useParams } from "react-router-dom";

import { CrmCard } from "../components/crm/CrmCard";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { DeletedInSalesforceBadge } from "../components/crm/DeletedInSalesforceBadge";
import { InventoryHexnode, inventoryHexnodeUrl } from "../components/logistics/InventoryHexnode";
import { InventoryTypeIcon } from "../components/logistics/InventoryTypeIcon";
import { InventoryWorkspaceOne, inventoryWorkspaceOneUrl } from "../components/logistics/InventoryWorkspaceOne";
import { RequirePermission } from "../components/RequirePermission";
import { LOGISTICS_INVENTORY_ROUTE, ResourceName } from "../constants/resources";
import {
  CrmObject,
  LogisticsInventoryItemDocument,
  type LogisticsInventoryItemQuery,
  type LogisticsInventoryItemQueryVariables,
  LogisticsWhereverSimDetailsDocument,
  type LogisticsWhereverSimDetailsQuery,
  type LogisticsWhereverSimDetailsQueryVariables,
  LogisticsWhereverSimDetailsState,
} from "../generated/graphql";
import { useSetBreadcrumbTitle } from "../layout/BreadcrumbTitleContext";
import { CRM_COLORS, crmRecordPath, formatCrmDate, formatCrmDateTime } from "../lib/crm";
import { flattenProviderData, inventoryIdentifier, SIM_TYPE, simLastOnlineText } from "../lib/logistics";

type Item = NonNullable<LogisticsInventoryItemQuery["logisticsInventoryItem"]>;
type ItemLocation = NonNullable<Item["location"]>;
type HistoryEntry = Item["history"][number];
type SimProvider = NonNullable<Item["simProvider"]>;
type LiveSection = LogisticsWhereverSimDetailsQuery["logisticsWhereverSimDetails"]["sim"];
type QualityCheckKey = Exclude<keyof Item["qualityCheck"], "__typename">;

const QUALITY_CHECKS: QualityCheckKey[] = [
  "sticker",
  "mdmRemoved",
  "knoxRemoved",
  "noOpenAndroidUpdates",
  "unknownSourcesAllowed",
  "accessPointConfigured",
  "roamingActive",
  "screenTimeout30Min",
  "appAsStartApp",
  "cameraInAppAllowed",
];

const EMPTY = "–";
const cellSx = { fontSize: "13px", py: "8px", px: "12px", color: CRM_COLORS.text, whiteSpace: "nowrap" } as const;

// A link to the CRM copy of a Salesforce record, or its name alone for staff
// without the CRM. The name falls back to the id while the crm copy lacks it.
const CrmLink = ({
  object,
  salesforceId,
  name,
  canOpenCrm,
}: {
  object: CrmObject;
  salesforceId?: string | null;
  name?: string | null;
  canOpenCrm: boolean;
}) => {
  if (!salesforceId) return <>{name ?? EMPTY}</>;
  const label = name ?? salesforceId;
  return canOpenCrm ? (
    <Link component={RouterLink} to={crmRecordPath(object, salesforceId)} sx={{ color: CRM_COLORS.link }}>
      {label}
    </Link>
  ) : (
    <>{label}</>
  );
};

const Fields = ({ fields }: { fields: [label: string, value: ReactNode][] }) => (
  <Box
    component="dl"
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(140px, max-content) 1fr",
      columnGap: "16px",
      rowGap: "6px",
      m: 0,
    }}
  >
    {fields.map(([label, value]) => (
      <Box key={label} sx={{ display: "contents" }}>
        <Typography component="dt" sx={{ fontSize: "13px", color: CRM_COLORS.label }}>
          {label}
        </Typography>
        <Box
          component="dd"
          aria-label={label}
          sx={{ m: 0, fontSize: "13px", color: CRM_COLORS.text, overflowWrap: "anywhere" }}
        >
          {value ?? EMPTY}
        </Box>
      </Box>
    ))}
  </Box>
);

const Location = ({ location, canOpenCrm }: { location: ItemLocation | null; canOpenCrm: boolean }) => {
  const translate = useTranslate();
  if (!location) {
    return (
      <Box data-testid="inventory-location">
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: CRM_COLORS.text }}>
          {translate("logistics.item.location.notAssigned")}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
          {translate("logistics.item.location.notAssignedHint")}
        </Typography>
      </Box>
    );
  }
  const { asset } = location;
  const assetName = asset && (asset.name ?? translate("logistics.item.location.unnamedAsset"));
  const order = asset?.latestOrder;
  return (
    <Box data-testid="inventory-location">
      <Fields
        fields={[
          [
            translate("logistics.item.location.account"),
            <CrmLink
              object={CrmObject.Account}
              salesforceId={location.accountSalesforceId}
              name={location.accountName}
              canOpenCrm={canOpenCrm}
            />,
          ],
          [
            translate("logistics.item.location.contract"),
            <CrmLink
              object={CrmObject.Contract}
              salesforceId={location.contractSalesforceId}
              name={location.contractNumber}
              canOpenCrm={canOpenCrm}
            />,
          ],
          [
            translate("logistics.item.location.contractItem"),
            <>
              <CrmLink
                object={CrmObject.ContractItem}
                salesforceId={location.contractItemSalesforceId}
                name={location.contractItemName}
                canOpenCrm={canOpenCrm}
              />
              {location.contractItemStatus && ` (${location.contractItemStatus})`}
            </>,
          ],
          [
            translate("logistics.item.location.atClientDate"),
            location.atClientDate && formatCrmDate(location.atClientDate),
          ],
          [
            translate("logistics.item.location.asset"),
            asset ? (
              <>
                {asset.salesforceUrl ? (
                  <Link
                    href={asset.salesforceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: CRM_COLORS.link }}
                  >
                    {assetName}
                  </Link>
                ) : (
                  assetName
                )}{" "}
                {translate(`logistics.item.location.slot.${asset.slot}`)}
              </>
            ) : (
              translate("logistics.item.location.noAsset")
            ),
          ],
          ...(asset
            ? ([
                [
                  translate("logistics.item.location.order"),
                  order ? (
                    <CrmLink
                      object={CrmObject.Order}
                      salesforceId={order.salesforceId}
                      name={order.orderNumber ?? order.name}
                      canOpenCrm={canOpenCrm}
                    />
                  ) : (
                    translate("logistics.item.location.noOrder")
                  ),
                ],
              ] as [string, ReactNode][])
            : []),
          ...(order
            ? ([
                [translate("logistics.item.location.recordType"), order.recordType],
                [translate("logistics.item.location.orderStatus"), order.status],
                [
                  translate("logistics.item.location.shippingDate"),
                  order.shippingDate && formatCrmDate(order.shippingDate),
                ],
                [
                  translate("logistics.item.location.completedDate"),
                  order.completedDate && formatCrmDate(order.completedDate),
                ],
              ] as [string, ReactNode][])
            : []),
        ]}
      />
    </Box>
  );
};

// What WhereverSIM's latest complete scan and connectivity check say of a SIM unit.
const WhereverSim = ({ simProvider }: { simProvider: SimProvider | null | undefined }) => {
  const translate = useTranslate();
  if (!simProvider) {
    return (
      <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
        {translate("logistics.sim.notScanned")}
      </Typography>
    );
  }
  const scannedAt: [string, ReactNode] = [
    translate("logistics.sim.fields.scannedAt"),
    formatCrmDateTime(simProvider.scannedAt),
  ];
  if (!simProvider.listed) {
    return (
      <Box>
        <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak, mb: "6px" }}>
          {translate("logistics.sim.notListed")}
        </Typography>
        <Fields fields={[scannedAt]} />
      </Box>
    );
  }
  const notChecked = translate("logistics.sim.notChecked");
  return (
    <Fields
      fields={[
        [translate("logistics.sim.fields.status"), simProvider.status ?? EMPTY],
        [translate("logistics.sim.fields.connectivity"), simProvider.connectivityStatus ?? notChecked],
        [translate("logistics.sim.fields.lastOnline"), simLastOnlineText(translate, simProvider)],
        scannedAt,
        [
          translate("logistics.sim.fields.checkedAt"),
          simProvider.checkedAt ? formatCrmDateTime(simProvider.checkedAt) : notChecked,
        ],
      ]}
    />
  );
};

// One call to WhereverSIM: every field it returned as a row, or why it did not.
const LiveSectionRows = ({
  title,
  testId,
  section,
}: {
  title: string;
  testId: string;
  section: LiveSection | undefined;
}) => {
  const translate = useTranslate();
  let body: ReactNode;
  if (!section) {
    body = (
      <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
        {translate("logistics.sim.live.noDevice")}
      </Typography>
    );
  } else if (section.errorCode) {
    body = (
      <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
        {translate("logistics.sim.live.loadError", { code: section.errorCode })}
      </Typography>
    );
  } else {
    const rows = flattenProviderData(section.data);
    body =
      rows.length === 0 ? (
        <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>{EMPTY}</Typography>
      ) : (
        <Fields
          fields={rows.map(([path, value]): [string, ReactNode] => [
            path,
            typeof value === "boolean" ? translate(value ? "logistics.item.yes" : "logistics.item.no") : value,
          ])}
        />
      );
  }
  return (
    <Box data-testid={testId}>
      <Typography component="h3" sx={{ fontSize: "13px", fontWeight: 700, color: CRM_COLORS.text, mb: "6px" }}>
        {title}
      </Typography>
      {body}
    </Box>
  );
};

// Everything WhereverSIM returns for a SIM unit, read live. A query of its own: the rest of the page does
// not wait for it and does not break on it. It shows nothing when WhereverSIM has nothing to say of the
// unit: no SIM, a list not read yet, or an ICCID the list does not hold.
const WhereverSimLive = ({ id }: { id: string }) => {
  const translate = useTranslate();
  const { data, loading, error } = useQuery<
    LogisticsWhereverSimDetailsQuery,
    LogisticsWhereverSimDetailsQueryVariables
  >(LogisticsWhereverSimDetailsDocument, { variables: { id }, fetchPolicy: "network-only" });
  const details = data?.logisticsWhereverSimDetails;
  let body: ReactNode;
  if (error) {
    body = (
      <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
        {translate("logistics.sim.live.loadError", { code: "graphql" })}
      </Typography>
    );
  } else if (!details) {
    return loading ? (
      <CrmCard title={translate("logistics.item.sections.whereverSimLive")} testId="inventory-sim-live">
        <CircularProgress size={20} aria-label={translate("logistics.sim.live.loading")} />
      </CrmCard>
    ) : null;
  } else if (details.state === LogisticsWhereverSimDetailsState.NotConfigured) {
    body = (
      <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
        {translate("logistics.sim.live.notConfigured")}
      </Typography>
    );
  } else if (details.state === LogisticsWhereverSimDetailsState.Available) {
    body = (
      <Box sx={{ display: "grid", gap: "16px" }}>
        <LiveSectionRows
          title={translate("logistics.sim.live.sim")}
          testId="inventory-sim-live-sim"
          section={details.sim}
        />
        <LiveSectionRows
          title={translate("logistics.sim.live.endpoint")}
          testId="inventory-sim-live-endpoint"
          section={details.endpoint}
        />
        <LiveSectionRows
          title={translate("logistics.sim.live.connectivity")}
          testId="inventory-sim-live-connectivity"
          section={details.connectivity}
        />
      </Box>
    );
  } else {
    return null;
  }
  return (
    <CrmCard title={translate("logistics.item.sections.whereverSimLive")} testId="inventory-sim-live">
      {body}
    </CrmCard>
  );
};

const History = ({ history, canOpenCrm }: { history: HistoryEntry[]; canOpenCrm: boolean }) => {
  const translate = useTranslate();
  if (history.length === 0) {
    return (
      <Typography sx={{ p: "16px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
        {translate("logistics.item.history.empty")}
      </Typography>
    );
  }
  const columns = [
    "account",
    "contract",
    "contractItem",
    "shippingDate",
    "atClientDate",
    "removedFromClientDate",
    "inStockDate",
    "endUserChange",
  ] as const;
  const date = (value?: string | null) => (value ? formatCrmDate(value) : EMPTY);
  return (
    <TableContainer>
      <Table size="small" aria-label={translate("logistics.item.sections.history")}>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell
                key={column}
                sx={{ ...cellSx, backgroundColor: CRM_COLORS.tableHeader, color: CRM_COLORS.textWeak, fontWeight: 700 }}
              >
                {translate(`logistics.item.history.columns.${column}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((entry, index) => (
            <TableRow key={entry.salesforceId ?? index} data-testid="inventory-history-entry">
              <TableCell sx={cellSx}>
                <CrmLink
                  object={CrmObject.Account}
                  salesforceId={entry.accountSalesforceId}
                  name={entry.accountName}
                  canOpenCrm={canOpenCrm}
                />
                {entry.deletedInSalesforceAt && <DeletedInSalesforceBadge deletedAt={entry.deletedInSalesforceAt} />}
              </TableCell>
              <TableCell sx={cellSx}>
                <CrmLink
                  object={CrmObject.Contract}
                  salesforceId={entry.contractSalesforceId}
                  name={entry.contractNumber}
                  canOpenCrm={canOpenCrm}
                />
              </TableCell>
              <TableCell sx={cellSx}>
                <CrmLink
                  object={CrmObject.ContractItem}
                  salesforceId={entry.contractItemSalesforceId}
                  name={entry.contractItemName}
                  canOpenCrm={canOpenCrm}
                />
              </TableCell>
              <TableCell sx={cellSx}>{date(entry.shippingDate)}</TableCell>
              <TableCell sx={cellSx}>{date(entry.atClientDate)}</TableCell>
              <TableCell sx={cellSx}>{date(entry.removedFromClientDate)}</TableCell>
              <TableCell sx={cellSx}>{date(entry.inStockDate)}</TableCell>
              <TableCell sx={cellSx}>
                {entry.endUserChange === null || entry.endUserChange === undefined
                  ? EMPTY
                  : translate(entry.endUserChange ? "logistics.item.yes" : "logistics.item.no")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ItemContent = ({ item }: { item: Item }) => {
  const translate = useTranslate();
  const { canAccess: canOpenCrm } = useCanAccess({ action: "list", resource: ResourceName.Crm });
  const flag = (value?: boolean | null) =>
    value === null || value === undefined ? null : translate(value ? "logistics.item.yes" : "logistics.item.no");
  const moment = (value?: string | null) => value && formatCrmDateTime(value);
  const title = item.name ?? translate("logistics.inventory.unnamed");
  useSetBreadcrumbTitle(title);
  const check = item.qualityCheck;
  const hexnodeUrl = inventoryHexnodeUrl(item.type, item.hexnode);
  const workspaceOneUrl = inventoryWorkspaceOneUrl(item.type, item.workspaceOne);

  return (
    <Box>
      <Title title={title} />
      <CrmPageHeader
        icon={<Inventory2OutlinedIcon sx={{ fontSize: 32, color: CRM_COLORS.brand }} />}
        overline={[translate("logistics.inventory.title"), item.type].filter(Boolean).join(" · ")}
        title={title}
        meta={item.modifiedAt && translate("logistics.item.modifiedAt", { time: formatCrmDateTime(item.modifiedAt) })}
        actions={
          <>
            <Button
              component={RouterLink}
              to={LOGISTICS_INVENTORY_ROUTE}
              startIcon={<ArrowBackOutlinedIcon />}
              variant="outlined"
              size="small"
              sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
            >
              {translate("logistics.item.backToList")}
            </Button>
            {item.salesforceUrl && (
              <Button
                href={item.salesforceUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon />}
                sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
              >
                {translate("logistics.item.openInSalesforce")}
              </Button>
            )}
            {hexnodeUrl && (
              <Button
                href={hexnodeUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon />}
                sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
              >
                {translate("logistics.hexnode.open")}
              </Button>
            )}
            {workspaceOneUrl && (
              <Button
                href={workspaceOneUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon />}
                sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
              >
                {translate("logistics.workspaceOne.open")}
              </Button>
            )}
          </>
        }
      >
        <Box sx={{ px: "16px", pb: "16px" }}>
          <Fields
            fields={[
              [translate("logistics.item.fields.status"), item.status],
              [translate("logistics.item.fields.identifier"), inventoryIdentifier(item)],
              [translate("logistics.item.fields.appVersion"), item.appVersion],
            ]}
          />
        </Box>
      </CrmPageHeader>
      {item.deletedInSalesforceAt && (
        <Alert severity="warning" data-testid="inventory-deleted-in-salesforce" sx={{ mb: "12px" }}>
          {translate("logistics.item.deletedInSalesforce", { time: formatCrmDateTime(item.deletedInSalesforceAt) })}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" },
          gap: "12px",
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "grid", gap: "12px" }}>
          <CrmCard title={translate("logistics.item.sections.location")}>
            <Location location={item.location ?? null} canOpenCrm={canOpenCrm === true} />
          </CrmCard>
          <CrmCard title={translate("logistics.item.sections.history")} flush>
            <History history={item.history} canOpenCrm={canOpenCrm === true} />
          </CrmCard>
        </Box>
        <Box sx={{ display: "grid", gap: "12px" }}>
          {item.type === SIM_TYPE && (
            <CrmCard title={translate("logistics.item.sections.whereverSim")} testId="inventory-sim-provider">
              <WhereverSim simProvider={item.simProvider} />
            </CrmCard>
          )}
          {item.type === SIM_TYPE && <WhereverSimLive id={item.id} />}
          {item.type === "TV Box" && (
            <CrmCard title={translate("logistics.hexnode.title")} testId="inventory-hexnode">
              <InventoryHexnode hexnode={item.hexnode} />
            </CrmCard>
          )}
          {item.type === "Tablet" && (
            <CrmCard
              title={translate("logistics.workspaceOne.title")}
              icon={<InventoryTypeIcon type="Tablet" size={24} />}
              testId="inventory-workspace-one"
              flush
            >
              <InventoryWorkspaceOne workspaceOne={item.workspaceOne} />
            </CrmCard>
          )}
          <CrmCard title={translate("logistics.item.sections.unit")} testId="inventory-unit">
            <Fields
              fields={[
                [translate("logistics.item.fields.type"), item.type],
                [translate("logistics.item.fields.status"), item.status],
                [translate("logistics.item.fields.inactiveReason"), item.inactiveReason],
                [
                  translate("logistics.item.fields.inactiveDate"),
                  item.inactiveDate && formatCrmDate(item.inactiveDate),
                ],
                [translate("logistics.item.fields.imei"), item.imei],
                [translate("logistics.item.fields.serialNumber"), item.serialNumber],
                [translate("logistics.item.fields.hardwareSerialNumber"), item.hardwareSerialNumber],
                [translate("logistics.item.fields.simIccid"), item.simIccid],
                [translate("logistics.item.fields.newDevice"), flag(item.newDevice)],
                [translate("logistics.item.fields.lastInStockAt"), moment(item.lastInStockAt)],
                [translate("logistics.item.fields.lastInTransferAt"), moment(item.lastInTransferAt)],
                [translate("logistics.item.fields.appVersion"), item.appVersion],
                [translate("logistics.item.fields.appFlavour"), item.appFlavour],
                [translate("logistics.item.fields.contentVersion"), item.contentVersion],
                [translate("logistics.item.fields.createdAt"), moment(item.createdAt)],
              ]}
            />
          </CrmCard>
          <CrmCard title={translate("logistics.item.sections.qualityCheck")} testId="inventory-quality-check">
            <Fields
              fields={[
                [translate("logistics.item.qualityCheck.doneAt"), moment(check.doneAt)],
                [translate("logistics.item.qualityCheck.doneBy"), check.doneBy],
                ...QUALITY_CHECKS.map((key): [string, ReactNode] => [
                  translate(`logistics.item.qualityCheck.${key}`),
                  flag(check[key]),
                ]),
              ]}
            />
          </CrmCard>
        </Box>
      </Box>
    </Box>
  );
};

const LogisticsInventoryItem = ({ id }: { id: string }) => {
  const translate = useTranslate();
  const { data, loading, error } = useQuery<LogisticsInventoryItemQuery, LogisticsInventoryItemQueryVariables>(
    LogisticsInventoryItemDocument,
    { variables: { id }, fetchPolicy: "cache-and-network" }
  );
  const item = data?.logisticsInventoryItem;
  if (error) return <Alert severity="error">{translate("logistics.item.loadError")}</Alert>;
  if (loading && !item) return <CircularProgress aria-label={translate("logistics.item.loading")} />;
  if (!item) return <Alert severity="info">{translate("logistics.item.notFound")}</Alert>;
  return <ItemContent item={item} />;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const LogisticsInventoryItemRoute = () => {
  const translate = useTranslate();
  const { id } = useParams();
  // The API refuses an id that is no uuid; say "not found" instead.
  if (!id || !UUID_PATTERN.test(id)) return <Alert severity="info">{translate("logistics.item.notFound")}</Alert>;
  return <LogisticsInventoryItem id={id} />;
};

export const LogisticsInventoryItemPage = () => (
  <RequirePermission permission={ResourceName.LogisticsInventory}>
    <LogisticsInventoryItemRoute />
  </RequirePermission>
);
